import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import VendorCard from './components/VendorCard';
import BillCard from './components/BillCard';
import ExpenseCard from './components/ExpenseCard';
import PurchaseTable from './components/PurchaseTable';
import SpendingInsights from './components/SpendingInsights';
import QuickActions from './components/QuickActions';
import FilterPanel from './components/FilterPanel';
import PurchaseFormModal from './components/PurchaseFormModal';
import PurchaseActionDropdown from './components/PurchaseActionDropdown';
import BulkPaymentModal from './components/BulkPaymentModal';
import PayBillModal from './components/PayBillModal';
import DeleteConfirmationModal from '../../components/ui/DeleteConfirmationModal';
import VendorDeletionModal from '../../components/ui/VendorDeletionModal';
import { vendorService, billService, paymentMadeService, expenseService, recurringExpenseService, vendorCreditService, recurringBillService, purchaseSubscriptions } from '../../services/purchaseCenterService';

const PurchaseCenter = () => {
  const { user, userProfile, organizationId: authOrganizationId } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('vendors');
  const [viewMode, setViewMode] = useState('table');
  const [showFilters, setShowFilters] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showBulkPaymentModal, setShowBulkPaymentModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formType, setFormType] = useState('vendors');
  
  // Enhanced delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showVendorDeletionModal, setShowVendorDeletionModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [bulkDeleteItems, setBulkDeleteItems] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [vendorRelationshipData, setVendorRelationshipData] = useState(null);
  const [isAnalyzingVendor, setIsAnalyzingVendor] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState({
    vendor: '',
    category: '',
    dateFrom: '',
    dateTo: '',
    amountMin: '',
    amountMax: ''
  });
  
  // Data states
  const [vendorsData, setVendorsData] = useState([]);
  const [billsData, setBillsData] = useState([]);
  const [paymentsData, setPaymentsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expensesData, setExpensesData] = useState([]);
  const [recurringExpensesData, setRecurringExpensesData] = useState([]);
  const [vendorCreditsData, setVendorCreditsData] = useState([]);
  const [recurringBillsData, setRecurringBillsData] = useState([]);
  
  // Add separate state for bulk payment bills
  const [bulkPaymentBillsData, setBulkPaymentBillsData] = useState([]);
  const [loadingBulkPaymentBills, setLoadingBulkPaymentBills] = useState(false);

  // Add new state for payment modal
  const [showPayBillModal, setShowPayBillModal] = useState(false);
  const [billToPay, setBillToPay] = useState(null);

  // CRITICAL FIX: Get organization ID from the correct authentication structure
  const organizationId = authOrganizationId || userProfile?.organization_id || null;
  const userId = user?.id || null;

  // Enhanced error checking and early return
  useEffect(() => {
    if (!user) {
      console.debug('User not authenticated yet');
      return;
    }

    if (!organizationId) {
      console.warn('User authenticated but no organization found:', {
        user: !!user,
        userProfile: !!userProfile,
        hasOrganizations: userProfile?.hasOrganizations,
        isFallback: userProfile?.isFallback,
        organizationError: userProfile?.organizationError
      });
      
      // Don't fetch data if no organization - show appropriate message instead
      setLoading(false);
      setError('No organization found. Please contact support to set up your organization access.');
      return;
    }

    console.debug('Organization context established:', {
      organizationId,
      userId,
      hasProfile: !!userProfile
    });

    fetchData();
    setupSubscriptions();
    
    // Cleanup subscriptions on unmount
    return () => {
      if (window.purchaseSubscriptions) {
        window.purchaseSubscriptions?.unsubscribeAll();
      }
    };
  }, [organizationId, userId, activeTab, userProfile?.hasOrganizations]);

  const fetchData = async () => {
    if (!organizationId || !userId) {
      console.warn('Cannot fetch data - missing required context:', { organizationId, userId });
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const filters = {
        status: statusFilter,
        search: searchTerm,
        dateFrom: advancedFilters?.dateFrom,
        dateTo: advancedFilters?.dateTo,
        minAmount: advancedFilters?.amountMin,
        maxAmount: advancedFilters?.amountMax,
        vendorId: advancedFilters?.vendor,
        category: advancedFilters?.category
      };

      switch (activeTab) {
        case 'vendors': {
          const { data, error } = await vendorService?.getVendors(organizationId, filters);
          if (error) throw error;
          setVendorsData(data || []);
          setFilteredData(data || []);
          break;
        }
        case 'expenses': {
          const { data, error } = await expenseService?.getExpenses(organizationId, filters);
          if (error) throw error;
          setExpensesData(data || []);
          setFilteredData(data || []);
          break;
        }
        case 'recurring-expenses': {
          const { data, error } = await recurringExpenseService?.getRecurringExpenses(organizationId, filters);
          if (error) throw error;
          setRecurringExpensesData(data || []);
          setFilteredData(data || []);
          break;
        }
        case 'bills': case 'recurring-bills': {
          if (activeTab === 'bills') {
            const { data, error } = await billService?.getBills(organizationId, filters);
            if (error) throw error;
            setBillsData(data || []);
            setFilteredData(data || []);
          } else {
            const { data, error } = await recurringBillService?.getRecurringBills(organizationId, filters);
            if (error) throw error;
            setRecurringBillsData(data || []);
            setFilteredData(data || []);
          }
          break;
        }
        case 'payment-made': {
          const { data, error } = await paymentMadeService?.getPaymentsMade(organizationId, filters);
          if (error) throw error;
          setPaymentsData(data || []);
          setFilteredData(data || []);
          break;
        }
        case 'vendor-credit': {
          const { data, error } = await vendorCreditService?.getVendorCredits(organizationId, filters);
          if (error) throw error;
          setVendorCreditsData(data || []);
          setFilteredData(data || []);
          break;
        }
        default: {
          setFilteredData([]);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setError(error?.message || 'Failed to fetch data');
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  // Add separate function to fetch bills for bulk payment
  const fetchBillsForBulkPayment = async () => {
    if (!organizationId || !userId) {
      console.warn('Cannot fetch bills for bulk payment - missing required context:', { organizationId, userId });
      return;
    }
    
    setLoadingBulkPaymentBills(true);
    
    try {
      // Fetch bills with minimal filters for bulk payment - get payable bills only
      const filters = {
        // Only get bills that can be paid
        status: null // Let the modal filter by status
      };

      const { data, error } = await billService?.getBills(organizationId, filters);
      
      if (error) {
        console.error('Error fetching bills for bulk payment:', error);
        setBulkPaymentBillsData([]);
      } else {
        console.log('Fetched bills for bulk payment:', data?.length, 'bills');
        setBulkPaymentBillsData(data || []);
      }
    } catch (error) {
      console.error('Error fetching bills for bulk payment:', error);
      setBulkPaymentBillsData([]);
    } finally {
      setLoadingBulkPaymentBills(false);
    }
  };

  const setupSubscriptions = () => {
    if (!organizationId || !userId) {
      console.warn('Cannot setup subscriptions - missing required context:', { organizationId, userId });
      return;
    }
    
    // Clean up existing subscriptions
    if (window.purchaseSubscriptions) {
      window.purchaseSubscriptions?.unsubscribeAll();
    }

    // Setup new subscriptions
    const callbacks = {
      vendors: (payload) => {
        console.log('Vendor change detected:', payload);
        if (activeTab === 'vendors') {
          fetchData();
        }
      },
      bills: (payload) => {
        console.log('Bill change detected:', payload);
        if (activeTab === 'bills') {
          fetchData();
        }
      },
      expenses: (payload) => {
        console.log('Expense change detected:', payload);
        if (activeTab === 'expenses') {
          fetchData();
        }
      },
      recurringExpenses: (payload) => {
        console.log('Recurring expense change detected:', payload);
        if (activeTab === 'recurring-expenses') {
          fetchData();
        }
      },
      vendorCredits: (payload) => {
        console.log('Vendor credit change detected:', payload);
        if (activeTab === 'vendor-credit') {
          fetchData();
        }
      },
      recurringBills: (payload) => {
        console.log('Recurring bill change detected:', payload);
        if (activeTab === 'recurring-bills') {
          fetchData();
        }
      },
      payments: (payload) => {
        console.log('Payment change detected:', payload);
        if (activeTab === 'payment-made') {
          fetchData();
        }
      }
    };

    window.purchaseSubscriptions = purchaseSubscriptions?.subscribeToAllChanges(organizationId, callbacks);
  };

  // Apply local filters to data
  useEffect(() => {
    const currentData = getCurrentData();
    let filtered = [...currentData];

    // Apply search filter
    if (searchTerm?.trim()) {
      filtered = filtered?.filter(item => {
        const searchFields = getSearchableFields(item, activeTab);
        return searchFields?.some(field => 
          field?.toLowerCase()?.includes(searchTerm?.toLowerCase())
        );
      });
    }

    // Apply status filter
    if (statusFilter) {
      filtered = filtered?.filter(item => item?.status === statusFilter);
    }

    // Apply advanced filters
    if (advancedFilters?.vendor) {
      filtered = filtered?.filter(item => {
        const vendorName = item?.vendor?.vendor_name || item?.vendor_name || item?.name || '';
        return vendorName?.toLowerCase()?.includes(advancedFilters?.vendor?.toLowerCase());
      });
    }

    if (advancedFilters?.category) {
      filtered = filtered?.filter(item => {
        const category = item?.category || '';
        return category?.toLowerCase()?.includes(advancedFilters?.category?.toLowerCase());
      });
    }

    // Date range filter
    if (advancedFilters?.dateFrom || advancedFilters?.dateTo) {
      filtered = filtered?.filter(item => {
        const itemDate = item?.issue_date || item?.due_date || item?.payment_date || item?.created_at;
        if (!itemDate) return false;

        const itemDateObj = new Date(itemDate);
        const fromDate = advancedFilters?.dateFrom ? new Date(advancedFilters?.dateFrom) : null;
        const toDate = advancedFilters?.dateTo ? new Date(advancedFilters?.dateTo) : null;

        if (fromDate && itemDateObj < fromDate) return false;
        if (toDate && itemDateObj > toDate) return false;
        
        return true;
      });
    }

    // Amount range filter
    if (advancedFilters?.amountMin || advancedFilters?.amountMax) {
      filtered = filtered?.filter(item => {
        const amount = item?.total_amount || item?.amount_received || item?.credit_limit || 0;
        const minAmount = advancedFilters?.amountMin ? parseFloat(advancedFilters?.amountMin) : 0;
        const maxAmount = advancedFilters?.amountMax ? parseFloat(advancedFilters?.amountMax) : Number.MAX_SAFE_INTEGER;

        return amount >= minAmount && amount <= maxAmount;
      });
    }

    setFilteredData(filtered);
  }, [activeTab, searchTerm, statusFilter, advancedFilters, vendorsData, billsData, paymentsData, expensesData, recurringExpensesData, vendorCreditsData, recurringBillsData]);

  const getSearchableFields = (item, type) => {
    switch (type) {
      case 'vendors':
        return [
          item?.vendor_name || '',
          item?.email || '',
          item?.phone || '',
          item?.company_name || '',
          item?.vendor_number || ''
        ];
      case 'expenses':
        return [
          item?.expense_number || '',
          item?.description || '',
          item?.vendor?.vendor_name || '',
          item?.reference_number || '',
          item?.category || ''
        ];
      case 'recurring-expenses':
        return [
          item?.template_name || '',
          item?.template_number || '',
          item?.description || '',
          item?.vendor?.vendor_name || '',
          item?.category || ''
        ];
      case 'bills': case 'recurring-bills':
        return [
          item?.bill_number || item?.template_number || '',
          item?.vendor?.vendor_name || '',
          item?.reference_number || ''
        ];
      case 'payment-made':
        return [
          item?.payment_reference || '',
          item?.reference_number || '',
          item?.description || ''
        ];
      case 'vendor-credit':
        return [
          item?.credit_number || '',
          item?.reference_number || '',
          item?.description || '',
          item?.vendor?.vendor_name || ''
        ];
      default:
        return [];
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case 'vendors':
        return vendorsData;
      case 'expenses':
        return expensesData;
      case 'recurring-expenses':
        return recurringExpensesData;
      case 'bills':
        return billsData;
      case 'recurring-bills':
        return recurringBillsData;
      case 'payment-made':
        return paymentsData;
      case 'vendor-credit':
        return vendorCreditsData;
      default:
        return [];
    }
  };

  // Tab configuration with real data counts
  const tabs = [
    { 
      id: 'vendors', 
      label: 'Vendors', 
      icon: 'Building', 
      count: vendorsData?.length || 0,
      badge: vendorsData?.filter(v => v?.status === 'inactive')?.length || 0
    },
    { 
      id: 'expenses', 
      label: 'Expenses', 
      icon: 'Receipt', 
      count: expensesData?.length || 0,
      badge: expensesData?.filter(e => e?.status === 'pending_approval')?.length || 0
    },
    { 
      id: 'recurring-expenses', 
      label: 'Recurring Expenses', 
      icon: 'RefreshCw', 
      count: recurringExpensesData?.length || 0,
      badge: recurringExpensesData?.filter(re => re?.status === 'paused')?.length || 0
    },
    { 
      id: 'bills', 
      label: 'Bills', 
      icon: 'FileText', 
      count: billsData?.length || 0,
      badge: billsData?.filter(b => b?.status === 'pending_approval' || b?.status === 'overdue')?.length || 0
    },
    { 
      id: 'recurring-bills', 
      label: 'Recurring Bills', 
      icon: 'RotateCcw', 
      count: recurringBillsData?.length || 0,
      badge: recurringBillsData?.filter(rb => rb?.status === 'paused')?.length || 0
    },
    { 
      id: 'payment-made', 
      label: 'Payment Made', 
      icon: 'CreditCard', 
      count: paymentsData?.length || 0,
      badge: paymentsData?.filter(p => p?.status === 'pending' || p?.status === 'processing')?.length || 0
    },
    { 
      id: 'vendor-credit', 
      label: 'Vendor Credit', 
      icon: 'Banknote', 
      count: vendorCreditsData?.length || 0,
      badge: vendorCreditsData?.filter(vc => vc?.status === 'draft')?.length || 0
    }
  ];

  const handleSort = (field, direction) => {
    console.log('Sorting by:', field, direction);
    const sorted = [...filteredData]?.sort((a, b) => {
      const aValue = a?.[field];
      const bValue = b?.[field];
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return direction === 'asc' 
          ? aValue?.localeCompare(bValue)
          : bValue?.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      return 0;
    });
    
    setFilteredData(sorted);
  };

  const handleFilter = (filters) => {
    console.log('Applying filters:', filters);
    
    // CRITICAL FIX: Map status values to correct enum values for each entity type
    let mappedStatusFilter = '';
    if (filters?.status) {
      // Map status values based on active tab to prevent enum constraint violations
      const statusMappings = {
        vendors: {
          'active': 'active',
          'inactive': 'inactive', 
          'suspended': 'suspended',
          'archived': 'archived',
          // Remove invalid status values that don't exist in vendor_status enum
          'paid': '', // Don't filter vendors by 'paid' status - it's not valid'pending': '','pending_approval': '','approved': '','overdue': '','completed': '','processing': '','failed': '','rejected': '','paused': '','draft': '','issued': '','applied': '','cancelled': '','reimbursed': ''
        },
        bills: {
          'pending': 'pending_approval','pending_approval': 'pending_approval','approved': 'approved','paid': 'paid','partially_paid': 'partially_paid','overdue': 'overdue','cancelled': 'cancelled','draft': 'draft','rejected': 'rejected'
        },
        expenses: {
          'draft': 'draft','pending_approval': 'pending_approval','approved': 'approved','reimbursed': 'reimbursed','rejected': 'rejected','cancelled': 'cancelled'
        },
        'recurring-expenses': {
          'draft': 'draft','active': 'active','paused': 'paused','cancelled': 'cancelled','completed': 'completed'
        },
        'recurring-bills': {
          'draft': 'draft','pending_approval': 'pending_approval','approved': 'approved','cancelled': 'cancelled','rejected': 'rejected'
        },
        'payment-made': {
          'pending': 'pending','cleared': 'cleared','bounced': 'bounced','cancelled': 'cancelled','refunded': 'refunded'
        },
        'vendor-credit': {
          'draft': 'draft','issued': 'issued','applied': 'applied','cancelled': 'cancelled','expired': 'expired'
        }
      };
      
      const entityMappings = statusMappings?.[activeTab] || {};
      mappedStatusFilter = entityMappings?.[filters?.status] || '';
      
      // Log the mapping for debugging
      console.log('Status mapping:', {
        activeTab,
        originalStatus: filters?.status,
        mappedStatus: mappedStatusFilter,
        isValidMapping: Boolean(mappedStatusFilter)
      });
      
      // If the status is not valid for the current entity, clear it to avoid enum errors
      if (filters?.status && !mappedStatusFilter) {
        console.warn(`Invalid status "${filters?.status}" for entity type "${activeTab}". Clearing status filter to prevent enum constraint violation.`);
      }
    }
    
    // Handle status filter with mapped values
    setStatusFilter(mappedStatusFilter);
    
    // Handle advanced filters
    setAdvancedFilters({
      vendor: filters?.vendor || '',
      category: filters?.category || '',
      dateFrom: filters?.dateFrom || '',
      dateTo: filters?.dateTo || '',
      amountMin: filters?.amountMin || '',
      amountMax: filters?.amountMax || ''
    });

    // Refetch data with new filters
    fetchData();
  };

  const handleClearFilters = () => {
    console.log('Clearing all filters');
    setSearchTerm('');
    setStatusFilter('');
    setAdvancedFilters({
      vendor: '',
      category: '',
      dateFrom: '',
      dateTo: '',
      amountMin: '',
      amountMax: ''
    });
    
    // Refetch data without filters
    fetchData();
  };

  const handleAction = async (action, item) => {
    console.log('Action:', action, 'Item:', item);
    
    if (!organizationId || !userId) {
      console.error('Missing organization ID or user ID:', { organizationId, userId });
      setError('Authentication context missing. Please refresh the page or sign in again.');
      return;
    }
    
    switch (action) {
      case 'edit':
        setSelectedItem(item);
        setFormType(activeTab);
        setShowFormModal(true);
        break;
      case 'view': console.log('Viewing item:', item);
        break;
      case 'approve': 
        let approveResult;
        try {
          if (activeTab === 'bills' && item?.id) {
            approveResult = await billService?.approveBill(item?.id, organizationId, userId);
          } else if (activeTab === 'expenses' && item?.id) {
            approveResult = await expenseService?.approveExpense(item?.id, organizationId, userId);
          } else if (activeTab === 'vendor-credit' && item?.id) {
            approveResult = await vendorCreditService?.approveVendorCredit(item?.id, organizationId, userId);
          } else if (activeTab === 'recurring-bills' && item?.id) {
            approveResult = await recurringBillService?.activateRecurringBill(item?.id, organizationId, userId);
          }
          
          if (approveResult?.error) throw approveResult?.error;
          
          // Refresh data
          fetchData();
        } catch (error) {
          console.error('Error approving item:', error);
          setError('Failed to approve item');
        }
        break;
      case 'pay': 
        // CRITICAL FIX: Implement actual bill payment functionality
        console.log('Paying bill:', item);
        if (activeTab === 'bills' && item?.id) {
          setBillToPay(item);
          setShowPayBillModal(true);
        } else {
          setError('Payment functionality is only available for bills.');
        }
        break;
      case 'duplicate': 
        setSelectedItem({ ...item, id: null });
        setFormType(activeTab);
        setShowFormModal(true);
        break;
      case 'delete':
        // Enhanced vendor deletion handling
        if (activeTab === 'vendors') {
          await handleVendorDeletion(item);
        } else {
          // Use regular delete modal for non-vendor items
          setItemToDelete(item);
          setBulkDeleteItems([]);
          setShowDeleteModal(true);
        }
        break;
      case 'bulk-delete':
        // Enhanced bulk deletion handling
        if (activeTab === 'vendors' && Array.isArray(item) && item?.length > 0) {
          // For bulk vendor deletion, analyze each vendor first
          await handleBulkVendorDeletion(item);
        } else {
          // Use regular bulk delete modal for non-vendor items
          setBulkDeleteItems(Array.isArray(item) ? item : [item]);
          setItemToDelete(null);
          setShowDeleteModal(true);
        }
        break;
      case 'contact': console.log('Contacting vendor:', item);
        break;
      case 'refund': console.log('Processing refund:', item);
        break;
      case 'void': console.log('Voiding payment:', item);
        break;
      case 'apply': console.log('Applying credit:', item);
        break;
      case 'reject': console.log('Rejecting credit:', item);
        break;
      case 'pause': 
        // Handle pausing recurring items
        if (activeTab === 'recurring-expenses' && item?.id) {
          try {
            const { error } = await recurringExpenseService?.updateRecurringExpense(item?.id, { status: 'paused' }, organizationId);
            if (error) throw error;
            fetchData();
          } catch (error) {
            console.error('Error pausing recurring expense:', error);
            setError('Failed to pause recurring expense');
          }
        } else if (activeTab === 'recurring-bills' && item?.id) {
          try {
            const { error } = await recurringBillService?.updateRecurringBill(item?.id, { status: 'paused' }, organizationId);
            if (error) throw error;
            fetchData();
          } catch (error) {
            console.error('Error pausing recurring bill:', error);
            setError('Failed to pause recurring bill');
          }
        }
        break;
      case 'resume': 
        // Handle resuming recurring items
        if (activeTab === 'recurring-expenses' && item?.id) {
          try {
            const { error } = await recurringExpenseService?.updateRecurringExpense(item?.id, { status: 'active' }, organizationId);
            if (error) throw error;
            fetchData();
          } catch (error) {
            console.error('Error resuming recurring expense:', error);
            setError('Failed to resume recurring expense');
          }
        } else if (activeTab === 'recurring-bills' && item?.id) {
          try {
            const { error } = await recurringBillService?.updateRecurringBill(item?.id, { status: 'active' }, organizationId);
            if (error) throw error;
            fetchData();
          } catch (error) {
            console.error('Error resuming recurring bill:', error);
            setError('Failed to resume recurring bill');
          }
        }
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  // Add new handler for processing individual bill payments
  const handleBillPayment = async (paymentData) => {
    if (!organizationId || !userId) {
      console.error('Missing organization ID or user ID:', { organizationId, userId });
      setError('Authentication context missing. Please refresh the page or sign in again.');
      return;
    }
    
    try {
      console.log('Processing individual bill payment:', paymentData);
      
      // Create the payment record
      let result = await paymentMadeService?.createPaymentMade(paymentData, organizationId, userId);
      
      if (result?.error) {
        throw result?.error;
      }
      
      console.log('Payment created successfully:', result?.data);
      
      // Update the bill status based on payment amount
      const billUpdate = {
        amount_paid: (billToPay?.amount_paid || 0) + parseFloat(paymentData?.amount_paid),
        updated_at: new Date()?.toISOString()
      };
      
      // Determine new bill status
      const totalPaid = billUpdate?.amount_paid;
      const totalAmount = billToPay?.total_amount || 0;
      
      if (totalPaid >= totalAmount) {
        billUpdate.status = 'paid';
      } else if (totalPaid > 0) {
        billUpdate.status = 'partially_paid';
      }
      
      // Update the bill
      const billUpdateResult = await billService?.updateBill(billToPay?.id, billUpdate, organizationId);
      
      if (billUpdateResult?.error) {
        console.warn('Payment created but bill status update failed:', billUpdateResult?.error);
      }
      
      console.log(`Payment of $${paymentData?.amount_paid} processed successfully for bill ${billToPay?.bill_number}`);
      
      // Refetch data to update UI
      await fetchData();
      
    } catch (error) {
      console.error('Error processing bill payment:', error);
      throw new Error(error?.message || 'Failed to process payment');
    }
  };

  // New method to handle individual vendor deletion
  const handleVendorDeletion = async (vendor) => {
    try {
      setIsAnalyzingVendor(true);
      setItemToDelete(vendor);

      // Analyze vendor relationships before showing deletion modal
      console.log('Analyzing vendor relationships for deletion:', vendor?.id);
      
      const analysisResult = await vendorService?.getVendorRelationships(
        vendor?.id, 
        organizationId
      );

      if (analysisResult?.error) {
        console.error('Error analyzing vendor relationships:', analysisResult?.error);
        setError('Failed to analyze vendor relationships. Please try again.');
        return;
      }

      const relationshipData = analysisResult?.data;
      console.log('Vendor relationship analysis:', relationshipData);

      setVendorRelationshipData(relationshipData);

      // Show appropriate modal based on whether vendor has constraints
      if (relationshipData?.has_constraints) {
        setShowVendorDeletionModal(true);
      } else {
        // No constraints, use regular delete modal
        setShowDeleteModal(true);
      }

    } catch (error) {
      console.error('Error in handleVendorDeletion:', error);
      setError('Failed to analyze vendor for deletion. Please try again.');
    } finally {
      setIsAnalyzingVendor(false);
    }
  };

  // New method to handle bulk vendor deletion
  const handleBulkVendorDeletion = async (vendorIds) => {
    try {
      setIsAnalyzingVendor(true);
      
      console.log('Analyzing bulk vendor relationships for deletion:', vendorIds);
      
      // Analyze each vendor's relationships
      const analyses = await Promise.all(
        vendorIds?.map(async (vendorId) => {
          try {
            let result = await vendorService?.getVendorRelationships(
              vendorId, 
              organizationId
            );
            return {
              vendorId,
              analysis: result?.data,
              error: result?.error
            };
          } catch (error) {
            return {
              vendorId,
              analysis: null,
              error: error
            };
          }
        })
      );

      // Check if any vendors have constraints
      const vendorsWithConstraints = analyses?.filter(a => a?.analysis?.has_constraints);
      const vendorsWithoutConstraints = analyses?.filter(a => !a?.analysis?.has_constraints && !a?.error);
      const failedAnalyses = analyses?.filter(a => a?.error);

      if (failedAnalyses?.length > 0) {
        console.warn('Some vendor analyses failed:', failedAnalyses);
      }

      if (vendorsWithConstraints?.length > 0) {
        // Some vendors have constraints - show error with suggestion
        const totalConstrainedVendors = vendorsWithConstraints?.length;
        const totalRelatedRecords = vendorsWithConstraints?.reduce((sum, v) => {
          const records = v?.analysis?.related_records || {};
          return sum + Object.values(records)?.reduce((recordSum, count) => recordSum + (count || 0), 0);
        }, 0);

        setError(
          `Cannot delete ${totalConstrainedVendors} vendors with ${totalRelatedRecords} related records. ` +
          `Please delete vendors individually to handle constraints, or archive them instead.`
        );
        return;
      }

      // All vendors are safe to delete - proceed with bulk deletion
      setBulkDeleteItems(vendorIds);
      setItemToDelete(null);
      setShowDeleteModal(true);

    } catch (error) {
      console.error('Error in handleBulkVendorDeletion:', error);
      setError('Failed to analyze vendors for bulk deletion. Please try again.');
    } finally {
      setIsAnalyzingVendor(false);
    }
  };

  // Enhanced delete confirmation handler
  const handleDeleteConfirm = async () => {
    if (!organizationId || !userId) {
      console.error('Cannot delete - missing required context:', { organizationId, userId });
      setError('Authentication context missing. Please refresh the page or sign in again.');
      return;
    }
    
    setIsDeleting(true);
    
    try {
      if (bulkDeleteItems?.length > 0) {
        // Bulk delete - enhanced error handling
        const promises = bulkDeleteItems?.map(async (itemId) => {
          switch (activeTab) {
            case 'vendors':
              return vendorService?.deleteVendor(itemId, organizationId, { forceDelete: false });
            case 'expenses':
              return expenseService?.deleteExpense(itemId, organizationId);
            case 'recurring-expenses':
              return recurringExpenseService?.deleteRecurringExpense(itemId, organizationId);
            case 'bills':
              return billService?.deleteBill(itemId, organizationId);
            case 'recurring-bills':
              return recurringBillService?.deleteRecurringBill(itemId, organizationId);
            case 'payment-made':
              return paymentMadeService?.deletePaymentMade(itemId, organizationId);
            case 'vendor-credit':
              return vendorCreditService?.deleteVendorCredit(itemId, organizationId);
            default:
              return Promise.resolve({ data: null, error: 'Not implemented' });
          }
        });
        
        const results = await Promise.all(promises);
        const errors = results?.filter(result => result?.error);
        const constraintViolations = errors?.filter(r => r?.error?.type === 'constraint_violation');
        const otherErrors = errors?.filter(r => r?.error?.type !== 'constraint_violation');
        
        if (constraintViolations?.length > 0) {
          console.error('Some items failed to delete due to constraints:', constraintViolations);
          
          // Create detailed error message for constraint violations
          const constraintDetails = constraintViolations?.map(r => {
            const relatedCount = r?.error?.relationshipData?.related_records ? 
              Object.values(r?.error?.relationshipData?.related_records)?.reduce((sum, count) => sum + (count || 0), 0) : 0;
            return `${relatedCount} related records`;
          });
          
          setError(
            `${constraintViolations?.length} items could not be deleted due to related records. ` +
            `Consider archiving these items instead. Details: ${constraintDetails?.join(', ')}`
          );
        } else if (otherErrors?.length > 0) {
          console.error('Some items failed to delete:', otherErrors);
          setError(`Failed to delete ${otherErrors?.length} items due to other errors.`);
        } else {
          console.log(`Successfully deleted ${bulkDeleteItems?.length} ${activeTab}`);
        }
      } else if (itemToDelete) {
        // Single delete - enhanced error handling
        let result;
        
        switch (activeTab) {
          case 'vendors':
            result = await vendorService?.deleteVendor(itemToDelete?.id, organizationId, { forceDelete: false });
            break;
          case 'expenses':
            result = await expenseService?.deleteExpense(itemToDelete?.id, organizationId);
            break;
          case 'recurring-expenses':
            result = await recurringExpenseService?.deleteRecurringExpense(itemToDelete?.id, organizationId);
            break;
          case 'bills':
            result = await billService?.deleteBill(itemToDelete?.id, organizationId);
            break;
          case 'recurring-bills':
            result = await recurringBillService?.deleteRecurringBill(itemToDelete?.id, organizationId);
            break;
          case 'payment-made':
            result = await paymentMadeService?.deletePaymentMade(itemToDelete?.id, organizationId);
            break;
          case 'vendor-credit':
            result = await vendorCreditService?.deleteVendorCredit(itemToDelete?.id, organizationId);
            break;
          default:
            result = { data: null, error: 'Not implemented' };
        }
        
        if (result?.error) {
          if (result?.error?.type === 'constraint_violation') {
            // Enhanced constraint violation handling
            const relatedCount = result?.error?.relationshipData?.related_records ? 
              Object.values(result?.error?.relationshipData?.related_records)?.reduce((sum, count) => sum + (count || 0), 0) : 
              result?.error?.message?.match(/\d+/)?.[0] || 'some';
              
            setError(
              `${result?.error?.message} ` +
              `Consider archiving this ${activeTab?.slice(0, -1)} instead to preserve data integrity.`
            );
          } else {
            throw result?.error;
          }
        } else {
          console.log(`Successfully deleted ${activeTab}:`, itemToDelete?.vendor_name || itemToDelete?.bill_number || itemToDelete?.expense_number || itemToDelete?.template_name);
        }
      }
      
      // Refetch data
      fetchData();
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setShowVendorDeletionModal(false);
      setItemToDelete(null);
      setBulkDeleteItems([]);
      setVendorRelationshipData(null);
      
    } catch (error) {
      console.error('Error deleting item(s):', error);
      setError(error?.message || 'Failed to delete item(s)');
    } finally {
      setIsDeleting(false);
    }
  };

  // New handler for vendor archiving
  const handleArchiveVendor = async (vendorId) => {
    try {
      setIsDeleting(true);

      console.log('Archiving vendor:', vendorId);

      let result = await vendorService?.archiveVendor(vendorId, organizationId);

      if (result?.error) {
        throw result?.error;
      }

      console.log('Vendor archived successfully');
      
      // Refetch data to update the UI
      await fetchData();
      
      // Close modals
      setShowVendorDeletionModal(false);
      setItemToDelete(null);
      setVendorRelationshipData(null);
      
    } catch (error) {
      console.error('Error archiving vendor:', error);
      setError(error?.message || 'Failed to archive vendor');
    } finally {
      setIsDeleting(false);
    }
  };

  // New handler for cascading vendor deletion
  const handleCascadeDeleteVendor = async (vendorId) => {
    try {
      setIsDeleting(true);

      console.log('Cascading delete vendor:', vendorId);

      let result = await vendorService?.deleteVendorWithOptions(vendorId, organizationId, {
        deletionType: 'cascade',
        userId: userId
      });

      if (result?.error || !result?.data?.success) {
        throw new Error(result?.data?.error || result?.error?.message || 'Cascading deletion failed');
      }

      console.log('Vendor cascading deletion completed:', result?.data);
      
      // Refetch data to update the UI
      await fetchData();
      
      // Close modals
      setShowVendorDeletionModal(false);
      setItemToDelete(null);
      setVendorRelationshipData(null);
      
    } catch (error) {
      console.error('Error in cascading vendor deletion:', error);
      setError(error?.message || 'Failed to delete vendor and related records');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting && !isAnalyzingVendor) {
      setShowDeleteModal(false);
      setShowVendorDeletionModal(false);
      setItemToDelete(null);
      setBulkDeleteItems([]);
      setVendorRelationshipData(null);
    }
  };

  const getDeleteModalProps = () => {
    if (bulkDeleteItems?.length > 0) {
      return {
        title: "Delete Multiple Items",
        message: `Are you sure you want to delete ${bulkDeleteItems?.length} ${activeTab}? This action cannot be undone.`,
        itemName: `${bulkDeleteItems?.length} ${activeTab}`,
        itemType: activeTab?.slice(0, -1),
        isDangerous: bulkDeleteItems?.length > 5,
        additionalWarning: activeTab === 'vendors' 
          ? "Deleting vendors will affect related bills and expenses. Consider marking them as inactive instead."
          : activeTab === 'recurring-expenses' || activeTab === 'recurring-bills'
          ? "Deleting recurring templates will stop future automatic generation."
          : null
      };
    } else if (itemToDelete) {
      const itemName = itemToDelete?.vendor_name || itemToDelete?.bill_number || itemToDelete?.expense_number || itemToDelete?.template_name || itemToDelete?.credit_number || itemToDelete?.payment_reference || 'item';
      
      // CRITICAL FIX: Completely avoid any reference to bills_count column
      // Instead, use logical validation based on vendor status and business rules
      const isVendorWithPotentialBills = activeTab === 'vendors' && itemToDelete?.status === 'active';
      const hasSignificantCreditLimit = activeTab === 'vendors' && (
        (typeof itemToDelete?.credit_limit === 'number' && itemToDelete?.credit_limit > 0) ||
        (typeof itemToDelete?.credit_limit === 'string' && parseFloat(itemToDelete?.credit_limit) > 0)
      );
      
      return {
        title: `Delete ${activeTab?.slice(0, -1)}`,
        itemName,
        itemType: activeTab?.slice(0, -1),
        // Use business logic instead of bills_count to determine danger level
        isDangerous: isVendorWithPotentialBills,
        additionalWarning: hasSignificantCreditLimit
          ? `This vendor has a credit limit of $${(parseFloat(itemToDelete?.credit_limit) || 0)?.toLocaleString()}. Please verify all balances before deletion.`
          : activeTab === 'bills' && itemToDelete?.status !== 'paid'
          ? "This bill is not yet paid. Deleting it may affect your accounts payable records."
          : activeTab === 'expenses' && itemToDelete?.status === 'approved'
          ? "This expense has been approved. Deleting it may affect your expense reports."
          : activeTab === 'recurring-expenses' || activeTab === 'recurring-bills'
          ? "This will stop future automatic generation of this recurring item."
          : activeTab === 'vendors' && itemToDelete?.status === 'active'
          ? "This is an active vendor. Consider marking as inactive instead of deleting to preserve transaction history."
          : null
      };
    }
    return {};
  };

  const handleQuickAction = (action) => {
    console.log('Quick action:', action);
    switch (action) {
      case 'add-vendor':
        setSelectedItem(null);
        setFormType('vendors');
        setShowFormModal(true);
        break;
      case 'record-expense': 
        setSelectedItem(null);
        setFormType('expenses');
        setShowFormModal(true);
        break;
      case 'setup-recurring-expense':
        setSelectedItem(null);
        setFormType('recurring-expenses');
        setShowFormModal(true);
        break;
      case 'create-bill': 
        setSelectedItem(null);
        setFormType('bills');
        setShowFormModal(true);
        break;
      case 'setup-recurring-bill':
        setSelectedItem(null);
        setFormType('recurring-bills');
        setShowFormModal(true);
        break;
      case 'bulk-payment': console.log('Opening bulk payment modal');
        // Fetch bills specifically for bulk payment before opening modal
        fetchBillsForBulkPayment();
        setShowBulkPaymentModal(true);
        break;
      case 'process-payment':
        setSelectedItem(null);
        setFormType('payment-made');
        setShowFormModal(true);
        break;
      case 'process-credit': 
        setSelectedItem(null);
        setFormType('vendor-credit');
        setShowFormModal(true);
        break;
      default:
        console.log('Unknown quick action:', action);
    }
  };

  const handleSaveForm = async (formData) => {
    if (!organizationId || !userId) {
      console.error('Missing organization ID or user ID:', { organizationId, userId });
      setError('Authentication context missing. Please refresh the page or sign in again.');
      return;
    }
    
    console.log('Saving form data:', formData, 'Type:', formType);
    
    try {
      let result;
      
      if (selectedItem?.id) {
        // Update existing item
        switch (formType) {
          case 'vendors':
            result = await vendorService?.updateVendor(selectedItem?.id, formData, organizationId);
            break;
          case 'expenses':
            result = await expenseService?.updateExpense(selectedItem?.id, formData, organizationId);
            break;
          case 'recurring-expenses':
            result = await recurringExpenseService?.updateRecurringExpense(selectedItem?.id, formData, organizationId);
            break;
          case 'bills':
            result = await billService?.updateBill(selectedItem?.id, formData, organizationId);
            break;
          case 'recurring-bills':
            result = await recurringBillService?.updateRecurringBill(selectedItem?.id, formData, organizationId);
            break;
          case 'payment-made':
            result = await paymentMadeService?.updatePaymentMade(selectedItem?.id, formData, organizationId);
            break;
          case 'vendor-credit':
            result = await vendorCreditService?.updateVendorCredit(selectedItem?.id, formData, organizationId);
            break;
          default:
            console.log('Update not implemented for:', formType);
            setError(`Update operation not supported for ${formType}`);
            return;
        }
      } else {
        // Create new item
        console.log('Creating new item with data:', formData);
        
        switch (formType) {
          case 'vendors':
            result = await vendorService?.createVendor(formData, organizationId, userId);
            break;
          case 'expenses':
            result = await expenseService?.createExpense(formData, organizationId, userId);
            break;
          case 'recurring-expenses':
            result = await recurringExpenseService?.createRecurringExpense(formData, organizationId, userId);
            break;
          case 'bills': console.log('Creating bill with organizationId:', organizationId, 'userId:', userId);
            result = await billService?.createBill(formData, organizationId, userId);
            break;
          case 'recurring-bills':
            result = await recurringBillService?.createRecurringBill(formData, organizationId, userId);
            break;
          case 'payment-made':
            result = await paymentMadeService?.createPaymentMade(formData, organizationId, userId);
            break;
          case 'vendor-credit':
            result = await vendorCreditService?.createVendorCredit(formData, organizationId, userId);
            break;
          default:
            console.log('Create not implemented for:', formType);
            setError(`Create operation not supported for ${formType}`);
            return;
        }
      }
      
      console.log('Service call result:', result);
      
      if (result?.error) {
        console.error('Service error:', result?.error);
        
        // Handle specific error types
        if (typeof result?.error === 'string') {
          throw new Error(result?.error);
        } else if (result?.error?.message) {
          throw new Error(result?.error?.message);
        } else {
          throw new Error('Failed to save data. Please try again.');
        }
      }
      
      if (!result?.data) {
        console.warn('No data returned from service call');
        throw new Error('Save operation completed but no data was returned. Please refresh to see changes.');
      }
      
      console.log('Successfully saved:', result?.data);
      
      // Show success message for bills specifically
      if (formType === 'bills' && !selectedItem?.id) {
        console.log('Bill created successfully:', result?.data?.bill_number);
        // Could add a success toast here in the future
      }
      
      // Refetch data to update the UI
      await fetchData();
      
      // Close modal
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving form data:', error);
      
      // Set user-friendly error message
      let errorMessage = 'Failed to save data. Please try again.';
      
      if (error?.message) {
        errorMessage = error?.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      // Special handling for bill creation errors
      if (formType === 'bills') {
        if (errorMessage?.includes('vendor')) {
          errorMessage = 'Please select a valid vendor and try again.';
        } else if (errorMessage?.includes('total_amount')) {
          errorMessage = 'Please enter a valid total amount greater than 0.';
        } else if (errorMessage?.includes('organization')) {
          errorMessage = 'Organization access error. Please refresh the page and try again.';
        }
      }
      
      setError(errorMessage);
    }
  };

  const handleCloseModal = () => {
    setShowFormModal(false);
    setSelectedItem(null);
  };

  const handleCloseBulkPaymentModal = () => {
    setShowBulkPaymentModal(false);
  };

  const handleProcessBulkPayments = async (paymentData) => {
    if (!organizationId || !userId) {
      console.error('Missing organization ID or user ID:', { organizationId, userId });
      setError('Authentication context missing. Please refresh the page or sign in again.');
      return;
    }
    
    try {
      let result = await paymentMadeService?.processBulkPayments(paymentData, organizationId, userId);
      
      if (result?.error) {
        throw result?.error;
      }
      
      console.log('Successfully processed bulk payments:', result?.data);
      
      // Show success message
      const successMessage = `Successfully initiated ${paymentData?.billIds?.length} payments totaling $${paymentData?.totalAmount?.toLocaleString()}`;
      console.log(successMessage);
      
      // Refetch data
      fetchData();
      
      // Close modal
      handleCloseBulkPaymentModal();
      
      // Switch to payment-made tab to show the new payments
      setActiveTab('payment-made');
      
    } catch (error) {
      console.error('Error processing bulk payments:', error);
      setError(error?.message || 'Failed to process bulk payments');
    }
  };

  const handleCloseBillPaymentModal = () => {
    setShowPayBillModal(false);
    setBillToPay(null);
  };

  // Get vendor options for dropdowns
  const vendorOptions = vendorsData?.map(vendor => ({
    value: vendor?.id?.toString(),
    label: vendor?.vendor_name
  })) || [];

  const renderTabContent = () => {
    // Show loading while authentication is initializing
    if (loading || !user) {
      return (
        <div className="text-center py-12">
          <Icon name="Loader2" size={48} className="text-muted-foreground/30 mx-auto mb-4 animate-spin" />
          <h3 className="text-lg font-medium text-foreground mb-2">Loading...</h3>
          <p className="text-muted-foreground">Initializing purchase center...</p>
        </div>
      );
    }

    // Show organization setup message if no organization
    if (!organizationId) {
      return (
        <div className="text-center py-12">
          <Icon name="Building2" size={48} className="text-muted-foreground/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Organization Setup Required</h3>
          <p className="text-muted-foreground mb-4">
            {userProfile?.isFallback 
              ? "Unable to load organization data. Please check your connection and try again." :"No organization found. Please contact support to set up your organization access."}
          </p>
          {userProfile?.isFallback && (
            <Button onClick={() => window.location?.reload()}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Refresh Page
            </Button>
          )}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center py-12">
          <Icon name="AlertCircle" size={48} className="text-red-500/30 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">Error</h3>
          <p className="text-muted-foreground mb-4">{error}</p>
          <div className="space-x-2">
            <Button onClick={() => fetchData()}>
              <Icon name="RefreshCw" size={16} className="mr-2" />
              Retry
            </Button>
            <Button variant="outline" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      );
    }

    const dataToRender = filteredData;

    if (viewMode === 'cards') {
      return (
        <div className="space-y-4">
          {/* Search Bar for Card View */}
          <div className="flex items-center space-x-4 mb-6">
            <div className="flex-1">
              <Input
                type="search"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e?.target?.value)}
                className="w-full"
              />
            </div>
            <div className="w-48">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e?.target?.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
              >
                <option value="">All Status</option>
                {/* CRITICAL FIX: Show only valid status options based on active tab */}
                {activeTab === 'vendors' && (
                  <>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                    <option value="archived">Archived</option>
                  </>
                )}
                {(activeTab === 'bills' || activeTab === 'recurring-bills') && (
                  <>
                    <option value="draft">Draft</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="paid">Paid</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="rejected">Rejected</option>
                  </>
                )}
                {(activeTab === 'expenses' || activeTab === 'recurring-expenses') && (
                  <>
                    <option value="draft">Draft</option>
                    <option value="pending_approval">Pending Approval</option>
                    <option value="approved">Approved</option>
                    <option value="reimbursed">Reimbursed</option>
                    <option value="rejected">Rejected</option>
                    <option value="cancelled">Cancelled</option>
                    {activeTab === 'recurring-expenses' && (
                      <>
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                        <option value="completed">Completed</option>
                      </>
                    )}
                  </>
                )}
                {activeTab === 'payment-made' && (
                  <>
                    <option value="pending">Pending</option>
                    <option value="cleared">Cleared</option>
                    <option value="bounced">Bounced</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="refunded">Refunded</option>
                  </>
                )}
                {activeTab === 'vendor-credit' && (
                  <>
                    <option value="draft">Draft</option>
                    <option value="issued">Issued</option>
                    <option value="applied">Applied</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="expired">Expired</option>
                  </>
                )}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataToRender?.map((item) => {
              switch (activeTab) {
                case 'vendors':
                  return (
                    <VendorCard
                      key={item?.id}
                      vendor={item}
                      onEdit={handleAction}
                      onView={handleAction}
                      onContact={handleAction}
                      onDelete={handleAction}
                    />
                  );
                case 'bills': case 'recurring-bills':
                  return (
                    <BillCard
                      key={item?.id}
                      bill={item}
                      onApprove={handleAction}
                      onPay={handleAction}
                      onEdit={handleAction}
                      onView={handleAction}
                      onDelete={handleAction}
                      onPause={handleAction}
                      onResume={handleAction}
                      type={activeTab}
                    />
                  );
                case 'expenses': case 'recurring-expenses':
                  return (
                    <ExpenseCard
                      key={item?.id}
                      expense={item}
                      onEdit={handleAction}
                      onDelete={handleAction}
                      onDuplicate={handleAction}
                      onApprove={handleAction}
                      onPause={handleAction}
                      onResume={handleAction}
                      type={activeTab}
                    />
                  );
                case 'payment-made': case 'vendor-credit':
                  return (
                    <div key={item?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-semibold text-foreground">
                            {item?.payment_reference || item?.credit_number}
                          </h3>
                          <p className="text-muted-foreground text-sm">
                            {item?.customer?.customer_name || item?.vendor?.vendor_name || 'Unknown'}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            item?.status === 'completed' || item?.status === 'approved' || item?.status === 'cleared' || item?.status === 'issued'
                              ? 'bg-green-100 text-green-800'
                              : item?.status === 'pending' || item?.status === 'processing' || item?.status === 'draft' ? 'bg-yellow-100 text-yellow-800'
                              : item?.status === 'failed' || item?.status === 'rejected' || item?.status === 'bounced' || item?.status === 'cancelled' ? 'bg-red-100 text-red-800' 
                              : item?.status === 'applied' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {item?.status}
                          </span>
                        </div>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Amount:</span>
                          <span className="font-semibold">${(item?.amount_received || item?.amount)?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Date:</span>
                          <span>{item?.payment_date || item?.credit_date}</span>
                        </div>
                        {item?.payment_method && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Method:</span>
                            <span>{item?.payment_method}</span>
                          </div>
                        )}
                        {item?.reason && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Reason:</span>
                            <span className="text-sm">{item?.reason}</span>
                          </div>
                        )}
                        {item?.remaining_balance > 0 && activeTab === 'vendor-credit' && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Remaining Balance:</span>
                            <span className="font-semibold text-primary">${item?.remaining_balance?.toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex justify-end space-x-2">
                        <Button variant="outline" size="sm" onClick={() => handleAction('view', item)}>
                          View
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => handleAction('delete', item)}
                          className="text-error hover:text-error hover:bg-error/10"
                        >
                          Delete
                        </Button>
                        {activeTab === 'payment-made' && item?.status !== 'completed' && (
                          <Button variant="outline" size="sm" onClick={() => handleAction('void', item)}>
                            Void
                          </Button>
                        )}
                        {activeTab === 'vendor-credit' && item?.status === 'draft' && (
                          <>
                            <Button size="sm" onClick={() => handleAction('approve', item)}>
                              Approve
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleAction('reject', item)}>
                              Reject
                            </Button>
                          </>
                        )}
                        {activeTab === 'vendor-credit' && item?.status === 'issued' && item?.remaining_balance > 0 && (
                          <Button variant="outline" size="sm" onClick={() => handleAction('apply', item)}>
                            Apply Credit
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                default:
                  return null;
              }
            })}
            {dataToRender?.length === 0 && (
              <div className="col-span-full text-center py-12">
                <Icon name="Search" size={48} className="text-muted-foreground/30 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">No results found</h3>
                <p className="text-muted-foreground">
                  {searchTerm || statusFilter 
                    ? "Try adjusting your search terms or filters" 
                    : `No ${activeTab} available. Click "New" to create your first ${activeTab?.slice(0, -1)}.`}
                </p>
                {!searchTerm && !statusFilter && (
                  <Button 
                    onClick={() => {
                      setSelectedItem(null);
                      setFormType(activeTab);
                      setShowFormModal(true);
                    }}
                    className="mt-4"
                  >
                    <Icon name="Plus" size={16} className="mr-2" />
                    Create {activeTab?.slice(0, -1)}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <PurchaseTable
        data={dataToRender}
        type={activeTab}
        onSort={handleSort}
        onFilter={handleFilter}
        onAction={handleAction}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
      />
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <div className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground">Purchase Center</h1>
                <p className="text-muted-foreground mt-2">
                  Manage vendors, bills, expenses, payments, and credits with comprehensive purchase workflows
                </p>
              </div>
              <div className="flex items-center space-x-4">
                <PurchaseActionDropdown onAction={handleQuickAction} />
                <Button
                  variant="outline"
                  onClick={() => setShowFilters(!showFilters)}
                  className="flex items-center space-x-2"
                >
                  <Icon name="Filter" size={16} />
                  <span>Filters</span>
                  {(statusFilter || Object.values(advancedFilters)?.some(v => v !== '')) && (
                    <span className="ml-2 px-2 py-1 bg-primary text-primary-foreground text-xs rounded-full">
                      Active
                    </span>
                  )}
                </Button>
                <div className="flex items-center bg-muted rounded-lg p-1">
                  <Button
                    variant={viewMode === 'table' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('table')}
                  >
                    <Icon name="Table" size={16} />
                  </Button>
                  <Button
                    variant={viewMode === 'cards' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('cards')}
                  >
                    <Icon name="Grid3X3" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center space-x-2">
                <Icon name="AlertCircle" size={16} className="text-red-500" />
                <span className="text-red-700 font-medium">Error</span>
              </div>
              <p className="text-red-600 mt-1">{error}</p>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setError(null)}
                className="mt-2"
              >
                Dismiss
              </Button>
            </div>
          )}

          {/* Quick Actions */}
          <div className="mb-8">
            <QuickActions onAction={handleQuickAction} />
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <div className="mb-6">
              <FilterPanel
                onFilter={handleFilter}
                onClear={handleClearFilters}
              />
            </div>
          )}

          <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
            {/* Main Content */}
            <div className="xl:col-span-3">
              {/* Tabs */}
              <div className="bg-card border border-border rounded-lg mb-6">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <div className="flex flex-wrap gap-1">
                    {tabs?.map((tab) => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          activeTab === tab?.id
                            ? 'bg-primary text-primary-foreground'
                            : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                        }`}
                      >
                        <Icon name={tab?.icon} size={16} />
                        <span className="whitespace-nowrap">{tab?.label}</span>
                        <span className="text-xs bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full">
                          {tab?.count}
                        </span>
                        {tab?.badge > 0 && (
                          <span className="text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full">
                            {tab?.badge}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                  {renderTabContent()}
                </div>
              </div>
            </div>

            {/* Right Sidebar - Insights */}
            <div className="xl:col-span-1">
              <SpendingInsights propOrganizationId={organizationId} />
            </div>
          </div>

          {/* Purchase Form Modal */}
          <PurchaseFormModal
            isOpen={showFormModal}
            onClose={handleCloseModal}
            type={formType}
            item={selectedItem}
            onSave={handleSaveForm}
            vendorOptions={vendorOptions}
          />

          {/* Individual Bill Payment Modal */}
          <PayBillModal
            isOpen={showPayBillModal}
            onClose={handleCloseBillPaymentModal}
            bill={billToPay}
            onPaymentProcessed={handleBillPayment}
          />

          {/* Bulk Payment Modal */}
          <BulkPaymentModal
            isOpen={showBulkPaymentModal}
            onClose={handleCloseBulkPaymentModal}
            onProcessPayments={handleProcessBulkPayments}
            bills={bulkPaymentBillsData}
          />

          {/* Enhanced Delete Confirmation Modals */}
          <DeleteConfirmationModal
            isOpen={showDeleteModal}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            isLoading={isDeleting}
            {...getDeleteModalProps()}
          />

          {/* New Vendor Deletion Modal */}
          <VendorDeletionModal
            isOpen={showVendorDeletionModal}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            onArchive={handleArchiveVendor}
            onCascadeDelete={handleCascadeDeleteVendor}
            vendor={itemToDelete}
            relationshipData={vendorRelationshipData}
            isLoading={isDeleting}
          />
        </div>
      </main>
    </div>
  );
};

export default PurchaseCenter;